import {
  TaskTypeReadable,
  taskTypesCreateMutation,
  taskTypesDestroyMutation,
  taskTypesGenericListOptions,
  taskTypesGenericListQueryKey,
  taskTypesPartialUpdateMutation,
  taskTypesRetrieveOptions
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  FormModal,
  FormNomenclatureDefaultItem
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button } from 'antd'
import { Add, Edit, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'custom-task'

export const NomenclaturesCustomTask = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()

  const { notification } = App.useApp()

  const sorter = useTableSorter()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: customTask, isLoading } = useQuery(
    taskTypesGenericListOptions({ query: { ...defaultFilters, ...params } })
  )

  const deleteCustomTaskMutation = useMutation({
    ...taskTypesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([taskTypesGenericListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItem) {
      deleteCustomTaskMutation.mutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number, code: string) => MenuProps['items'] =
    useCallback(
      (id, code) => {
        const action = [
          {
            label: t('nomenclature.edit'),
            icon: <Edit size={20} />,
            key: 'edit',
            onClick: () => setEditItem(id)
          }
        ]

        return !code
          ? [
              ...action,
              { type: 'divider' },
              {
                label: t('nomenclature.delete'),
                icon: <Trash size={20} />,
                danger: true,
                key: 'delete',
                onClick: () => setDeleteItem(id)
              }
            ]
          : action
      },
      [t]
    )

  const columns: TableColumnsType<TaskTypeReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id, code }) => (
          <TableActions options={getActionColumnItem(id, code)} />
        )
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TableContainer
        data={customTask}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <Button
            type="primary"
            icon={<Add size={20} />}
            onClick={() => setIsCreate(true)}
          >
            {t('nomenclature.add_custom_task')}
          </Button>
        }
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={taskTypesRetrieveOptions}
        onCreate={taskTypesCreateMutation}
        onUpdate={taskTypesPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclatureDefaultItem />
      </FormModal>
    </>
  )
}
